import type { Matched } from "@nexxt/contracts";
import { Button, type GridColumn, gridColumns } from "@nexxt/ui";
import { m } from "../paraglide/messages.js";

// The filter "To do" of a list (M-11, ADR-302): the rows in one of the "your turn" queues of its place at least, the
// union the domain finds with `anyOf`, and why each row is there. The queues, their words and their filters are the
// app's; the same filter and the same column for every list.

interface TodoFilterProps {
  /** How many rows are to do: the number of the sidebar, the union of the queues. */
  count: number | undefined;
  active: boolean;
  /** `true` to keep only the rows to do, nothing for all of them: what the URL keeps. */
  onChange: (todo: true | undefined) => void;
}

export function TodoFilter({ count, active, onChange }: TodoFilterProps) {
  return (
    <Button
      variant={active ? "primary" : "secondary"}
      aria-pressed={active}
      onClick={() => onChange(active ? undefined : true)}
    >
      {count === undefined ? m.todo_filter() : m.todo_filter_count({ count })}
    </Button>
  );
}

/**
 * Why each row of the pages read is to do: the titles of the queues whose sets of `anyOf` it is in, in the order of
 * the queues (`titles[i]` is the title of the set `i`).
 */
function todoReasons(pages: readonly { matched?: Matched | undefined }[], titles: readonly string[]) {
  const matched: Matched = Object.assign({}, ...pages.map((page) => page.matched));
  return (id: string): string[] => (matched[id] ?? []).flatMap((set) => titles[set] ?? []);
}

/** The column "Reason" of a list filtered "To do": a row in two queues says both. */
function reasonColumn<T extends { id: string }>(reasonsOf: (id: string) => string[]): GridColumn<T> {
  return gridColumns<T>().display({
    id: "todoReason",
    header: () => m.todo_reason(),
    cell: ({ row }) => {
      const reasons = reasonsOf(row.original.id).join("; ");
      return (
        <span className="truncate" title={reasons}>
          {reasons}
        </span>
      );
    },
    meta: { width: "minmax(14rem, 2fr)" },
  });
}

/**
 * The columns of a list with "Reason" after the first one while the list is filtered "To do", that is with the
 * `titles` of its queues: the reasons of each row from the pages read.
 */
export function withReasonColumn<T extends { id: string }>(
  columns: readonly GridColumn<T>[],
  titles: readonly string[] | undefined,
  pages: readonly { matched?: Matched | undefined }[] | undefined,
): GridColumn<T>[] {
  if (!titles) return [...columns];
  return [...columns.slice(0, 1), reasonColumn<T>(todoReasons(pages ?? [], titles)), ...columns.slice(1)];
}
